import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Stack } from '@mui/material';
import { useNavigationStore } from 'store';
import {
  Wrapper,
  AnimatedImage,
  LoaderContainer,
} from '@/components/navigation/NavigationIntroScreen/styles';
import { ProgressBar } from '@/components/navigation/NavigationIntroScreen/components/ProgressBar';
import DeviceManager from '@/components/util/deviceManager';
import * as all from '@/components/util/all';
import { Tour } from '@/_models/Tour';
import { getAsset } from '@/utils/getAsset';

interface NavigationIntroScreenProps {
  appState: any;
  tour: Tour;
  onLoaded: () => void;
  logo: string;
  rtl?: boolean;
}

export const NavigationIntroScreen: React.FC<NavigationIntroScreenProps> = ({
  appState,
  tour,
  onLoaded = () => {},
  logo: footerLogo,
  rtl,
}) => {
  const [animateLogo, setAnimateLogo] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [choicePopupOpen, setChoicePopupOpen] = useState(false);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  const progressRef = useRef(progress);
  const appStateRef = useRef(appState);
  const isMobile = DeviceManager.isMobile();

  useEffect(() => {
    const LOGO_DURATION = 3000; // 3 seconds for logo animation
    const PROGRESS_DURATION = 9000; // 9 seconds for progress bar
    const TOTAL_MINIMUM_DURATION = LOGO_DURATION + PROGRESS_DURATION;
    const PROGRESS_BAR_STEP = 1700;

    let progressInterval: NodeJS.Timeout;
    let monitorInterval: NodeJS.Timeout;
    let isMinimumSequenceComplete = false;

    const logoTimeout = setTimeout(() => {
      setAnimateLogo(true);
    }, LOGO_DURATION);

    const progressTimeout = setTimeout(() => {
      setLoaderVisible(true);
      let currentProgress = 0;
      progressInterval = setInterval(() => {
        currentProgress += 20;
        if (currentProgress > 80) {
          setProgress(currentProgress);
          clearInterval(progressInterval);
          monitorInterval = setInterval(() => {
            if (isMinimumSequenceComplete) {
              setProgress(100);
              onLoaded();
            }
          }, 100);
        } else {
          setProgress(currentProgress);
        }
      }, PROGRESS_BAR_STEP);
    }, LOGO_DURATION);

    const minimumSequenceTimeout = setTimeout(() => {
      isMinimumSequenceComplete = true;
      if (progressRef.current === 100) {
        onLoaded();
      }
    }, TOTAL_MINIMUM_DURATION);

    return () => {
      clearTimeout(logoTimeout);
      clearTimeout(progressTimeout);
      clearTimeout(minimumSequenceTimeout);
      if (progressInterval) clearInterval(progressInterval);
      if (monitorInterval) clearInterval(monitorInterval);
    };
  }, []);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  const progressText = useMemo(() => {
    if (progress > 75) return translation?.navWillStartSoon || 'Your navigation will start soon';
    if (progress > 50) return translation?.navCalculatingPath || 'Calculating the path';
    if (progress > 25) return translation?.navLoadingWaypoints || 'Loading waypoints';
    return translation?.navLoading3dSpace || 'Loading the 3D space';
  }, [progress, translation]);

  const userLogo = tour.user.whitelabelId
    ? `${process.env.NEXT_PUBLIC_CDN}/users/${tour.user.id}/logo.png${all.getVersion(
        tour.user.updatedAt,
      )}`
    : getAsset('logo-black.svg');
  const logo = tour.client?.logo
    ? `${process.env.NEXT_PUBLIC_CDN}/clients/${tour.client?.id}/logo.png${all.getVersion(
        tour.client?.updatedAt,
      )}`
    : userLogo;

  return (
    <Wrapper style={{ zIndex: 9999 }}>
      <AnimatedImage src={logo} animate={animateLogo} style={{ position: 'absolute' }} />
      <LoaderContainer style={{ opacity: loaderVisible ? 1 : 0 }}>
        <img
          src="/images/loading.gif"
          alt=""
          style={{ width: isMobile ? '65%' : '300px' }}
          onClick={() => {
            setPopupOpen(!popupOpen);
          }}
        />
        <ProgressBar progress={progress} sx={{ marginTop: '16px' }} />
        <Typography
          variant="h4"
          fontWeight={600}
          lineHeight="30px"
          sx={{ marginTop: '16px', direction: rtl ? 'rtl' : 'ltr' }}
          onClick={() => {
            setChoicePopupOpen(!choicePopupOpen);
          }}
        >
          {progressText}
        </Typography>
        <Typography
          color="#667085"
          lineHeight="20px"
          sx={{ marginTop: '10px', direction: rtl ? 'rtl' : 'ltr' }}
        >
          {translation?.navWillTakeUp10Sec || 'This will take up to 10 seconds'}
        </Typography>
      </LoaderContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        alignSelf="center"
        sx={{
          width: '100%',
          gap: '7px',
          position: 'fixed',
          bottom: 0,
          // right: '20px',
          mt: '4px',
          mb: '20px',
          display: { xs: 'flex', sm: 'none' },
        }}
      >
        <Typography
          fontSize="16px"
          lineHeight="30px"
          fontWeight={400}
          sx={{ direction: rtl ? 'rtl' : 'ltr' }}
        >
          {translation?.navPowerBy || 'Powered by'}
        </Typography>
        <img className="watermark" width="81px" height="25.5px" alt="" src={footerLogo} />
      </Stack>
    </Wrapper>
  );
};
